<template>
    <div>
        <div class="row p-1">
            <div v-if="errors.seatingPlan || vueMultiSelect.isInvalid" class="alert alert-danger">
                <p v-if="errors.seatingPlan">
                    {{ errors.seatingPlan }}
                    <button type="button" class="booking-text-button" @click="scheduleChecks">{{ $t("reservationForm.retry") }}</button>
                </p>
                <p v-if="vueMultiSelect.isInvalid">{{ $tl("labels.booking.reservations.edit.tableNotAvailable", restaurant_id) }}</p>
            </div>
            <p v-if="seatingUnavailable && !reservation.ignore_placement && !hasTablesSelected" class="booking-alert" role="status">
                {{ $t("reservationForm.placementUnavailable") }}
            </p>
            <p v-if="capacityStatus === 'error'" class="booking-alert" role="alert">
                {{ $t("reservationForm.requestFailed") }}
                <button type="button" class="booking-text-button" @click="scheduleChecks">{{ $t("reservationForm.retry") }}</button>
            </p>
            <div class="col-12">
                <div v-if="reservation.service && reservation.service.is_table_rotation_enabled == true" class="booking-duration">
                    <label for="booking-duration" class="form-label"
                        >{{ $tl(`labels.booking.reservations.${isPassingCustomer ? "passingCustomer" : "edit"}.duration`) }} <small>*</small></label
                    >
                    <input
                        id="booking-duration"
                        type="time"
                        class="form-control"
                        step="900"
                        required
                        v-model="durationInput"
                        :disabled="hasTablesSelected"
                        aria-describedby="booking-duration-help"
                        @change="reservation.is_duration_customized = true" />
                    <small id="booking-duration-help" class="booking-help">{{
                        $tl(`infos.booking.reservations.edit${isPassingCustomer ? ".passingCustomer" : ""}.duration`)
                    }}</small>
                </div>
            </div>
            <div class="col-12 mt-2">
                <p v-if="reservation.service?.is_seating_plan_algorithm_enabled && !isPassingCustomer && !hasTablesSelected" class="booking-help">
                    <span v-if="loading.can_place || loading.scheduled">{{ $t("reservationForm.placementPending") }}</span>
                    <span v-else-if="can_place">{{ $tl("infos.booking.reservations.add.willBePlacedAuto", restaurant_id) }}</span>
                </p>
                <template v-if="reservation.service?.has_seating_plan && nb_pers_total > 0">
                    <ReservationTablePicker
                        :rooms="tableRooms"
                        :selected="vueMultiSelect.entitiesSelected"
                        :guests="nb_pers_total"
                        :disabled="readOnly || reservation.ignore_placement || holdsPending > 0"
                        @change="setMultiSelectSelectedEntities" />
                    <div v-if="canIgnoreTable">
                        <label class="booking-checkbox">
                            <input type="checkbox" :disabled="readOnly" :checked="reservation.ignore_placement" @change="toggleIgnorePlacement" />
                            {{ $tl("labels.booking.seatingPlan.ignorePlacement") }}
                        </label>
                        <p class="booking-help">{{ $tl("infos.booking.seatingPlan.ignorePlacement") }}</p>
                    </div>
                </template>
                <div v-else-if="reservation.service && nb_pers_total > 0" class="mb-2">
                    <label class="form-label" for="num_table">{{ $tl("labels.booking.seatingPlan.table.title") }}</label>
                    <input
                        id="num_table"
                        type="text"
                        v-model="reservation.num_table"
                        class="form-control"
                        autocomplete="off"
                        :disabled="readOnly"
                        :class="{ 'is-invalid': hasErrors('num_table') }" />
                    <div v-if="hasErrors('num_table')">
                        <div class="invalid-feedback d-block" v-for="error in formErrors.errors.num_table" :key="error">{{ error }}</div>
                    </div>
                </div>
            </div>

            <div class="col-md-6 mt-2"></div>
            <div class="col-12" v-if="reservation.service && reservation.slot && nb_pers_total > 0">
                <p v-if="loading.menus" class="booking-inline-status" role="status">{{ $tl("labels.loading") }}</p>
                <p v-else-if="errors.menus" class="booking-alert" role="alert">
                    {{ errors.menus }}
                    <button type="button" class="booking-text-button" @click="loadMenusData">{{ $t("reservationForm.retry") }}</button>
                </p>
                <div class="row" v-else-if="available_menus && available_menus.length > 0">
                    <div class="col-12">
                        <h4 class="booking-subsection-title">
                            {{ menusTitle }}
                        </h4>
                    </div>
                    <div class="col-12 mt-2">
                        <menus-reservation
                            class="ps-1"
                            :restaurant-id="Number.parseInt(restaurant_id)"
                            :formErrors="formErrors"
                            :available_menus="available_menus"
                            :reservation="reservation"
                            :nb_total="nb_pers_total" />
                    </div>
                </div>
            </div>
            <div class="col-12" v-if="reservation.service && reservation.slot && nb_pers_total > 0">
                <p v-if="loading.general_options" class="booking-inline-status" role="status">{{ $tl("labels.loading") }}</p>
                <p v-else-if="errors.general_options" class="booking-alert" role="alert">
                    {{ errors.general_options }}
                    <button type="button" class="booking-text-button" @click="loadGeneralOptionsData">{{ $t("reservationForm.retry") }}</button>
                </p>
                <div class="row" v-else-if="available_general_options && available_general_options.length > 0">
                    <div class="col-12">
                        <h4 class="booking-subsection-title">
                            {{ generalOptionsTitle }}
                        </h4>
                    </div>
                    <div class="col-12 mt-2">
                        <general-options-reservation
                            class="ps-1"
                            :restaurant-id="Number.parseInt(restaurant_id)"
                            :formErrors="formErrors"
                            :available_general_options="available_general_options"
                            :reservation="reservation"
                            :nb_total="nb_pers_total" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import ReservationTablePicker from "../ReservationTablePicker.vue";
import { buildTableRooms, selectedTableCapacity } from "../tableSelection.mjs";
import { createTableHolds } from "../tableHolds.mjs";
import { createRequestGate, guestCount, placementBlocker, withoutTrailingColon } from "../reservationFormState.mjs";
import menusReservation from "../menusReservation.vue";
import generalOptionsReservation from "../generalOptionsReservation.vue";

export default {
    data() {
        return {
            disposed: false,
            checksTimer: null,
            checksGate: createRequestGate(),
            resourcesController: new AbortController(),
            tableHolds: createTableHolds(axios),
            holdsPending: 0,
            capacityStatus: "checking",
            seatingUnavailable: false,
            loading: {
                menus: false,
                general_options: false,
                can_place: false,
                capacity: false,
                scheduled: false,
            },
            errors: {
                tables: null,
                menus: null,
                general_options: null,
                seatingPlan: null,
            },
            available_menus: null,
            available_general_options: null,
            socket: {
                listeningChannels: {
                    services: [],
                },
            },
            can_place: false,
        };
    },
    props: {
        baseReservation: {
            default: null,
        },
        vueMultiSelect: {
            type: Object,
            required: true,
        },
        restaurant_id: {
            default: null,
        },
        reservation: {
            type: Object,
            required: true,
        },
        formErrors: {
            default: null,
        },
        readOnly: {
            default: false,
        },
        tablesIdToPreselect: {
            default: () => {
                return [];
            },
        },
        isPassingCustomer: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        durationInput: {
            get() {
                return this.reservation.duration.HH + ":" + this.reservation.duration.mm;
            },
            set(value) {
                const [HH = "", mm = ""] = value.split(":");
                this.reservation.duration = { HH, mm };
            },
        },
        checkKey() {
            return [
                this.restaurant_id,
                this.reservation.reservation_date?.toISODate(),
                this.reservation.slot_id,
                this.nb_pers_total,
                this.reservation.duration?.HH,
                this.reservation.duration?.mm,
            ].join(":");
        },
        serviceContextKey() {
            return [this.restaurant_id, this.reservation.service?.id, this.reservation.reservation_date?.toISODate()].join(":");
        },
        isBusy() {
            return Object.values(this.loading).some(Boolean) || this.holdsPending > 0;
        },
        blocker() {
            return placementBlocker({
                busy: this.isBusy,
                loadFailed: Boolean(this.errors.seatingPlan || this.errors.menus || this.errors.general_options),
                tableUnavailable: Boolean(this.vueMultiSelect.isInvalid),
                ignorePlacement: this.reservation.ignore_placement,
                hasSelectedTables: this.vueMultiSelect.entitiesSelected.length > 0,
                canPlace: this.can_place,
            });
        },
        canContinue() {
            return this.blocker === null;
        },
        menusTitle() {
            return withoutTrailingColon(this.$tl("labels.booking.reservations.add.chooseMenus", this.restaurant_id));
        },
        generalOptionsTitle() {
            return withoutTrailingColon(this.$tl("labels.booking.reservations.add.chooseGeneralOptions"));
        },
        reservationStartsAt() {
            if (this.reservation.slot === null) {
                return this.reservation.reservation_date;
            }

            return this.reservation.reservation_date.set({
                hour: this.reservation.slot.hour_start.split(":")[0],
                minute: this.reservation.slot.hour_start.split(":")[1],
            });
        },
        reservationEndsAt() {
            if (this.reservationStartsAt && this.reservation.duration) {
                return this.reservationStartsAt.plus({
                    hours: this.reservation.duration.HH,
                    minutes: this.reservation.duration.mm,
                });
            }

            return null;
        },
        nb_pers_total() {
            return guestCount(this.reservation);
        },
        seatingPlan() {
            return this.reservation.service ? this.reservation.service.seatingPlanFilled : null;
        },
        tableRooms() {
            return buildTableRooms(this.seatingPlan, {
                slotId: this.reservation.slot_id,
                startsAt: this.reservationStartsAt,
                endsAt: this.reservationEndsAt,
                selected: this.vueMultiSelect.entitiesSelected,
                guests: this.nb_pers_total,
                baseTableIds: this.baseReservation?.tables.data.map((table) => table.id) ?? [],
            });
        },
        hasTablesSelected() {
            return this.seatingPlan ? this.vueMultiSelect.entitiesSelected.length > 0 : false;
        },
        canIgnoreTable() {
            if (this.loading.can_place) {
                return false;
            }

            return this.reservation.ignore_placement || (!this.can_place && this.vueMultiSelect.entitiesSelected.length === 0);
        },
    },
    methods: {
        toggleIgnorePlacement() {
            this.reservation.ignore_placement = !this.reservation.ignore_placement;
        },
        setMultiSelectSelectedEntities(value) {
            const key = (entity) => entity.type + ":" + entity.id;
            const old = this.vueMultiSelect.entitiesSelected;
            old.filter((entity) => !value.some((item) => key(item) === key(entity))).forEach((entity) => this.tableUnselected(entity));
            value.filter((entity) => !old.some((item) => key(item) === key(entity))).forEach((entity) => this.tableSelected(entity));
            this.vueMultiSelect.entitiesSelected = value;
        },
        async loadMenusData() {
            if (!this.reservation.slot) return;
            this.loading.menus = true;
            this.errors.menus = null;
            try {
                const response = await axios.get(
                    "/api/restaurants/" + this.restaurant_id + "/services/" + this.reservation.slot.service_id + "/menus?include=menu_options",
                    { signal: this.resourcesController.signal }
                );
                if (!this.disposed) this.available_menus = response.data.data;
            } catch (error) {
                if (!this.disposed) this.errors.menus = this.$t("reservationForm.menusLoadFailed");
            } finally {
                if (!this.disposed) this.loading.menus = false;
            }
        },
        async loadGeneralOptionsData() {
            if (!this.reservation.slot) return;
            this.loading.general_options = true;
            this.errors.general_options = null;
            try {
                const response = await axios.get(
                    "/api/restaurants/" + this.restaurant_id + "/services/" + this.reservation.slot.service_id + "/general_options",
                    { signal: this.resourcesController.signal }
                );
                if (!this.disposed) this.available_general_options = response.data.data;
            } catch (error) {
                if (!this.disposed) this.errors.general_options = this.$t("reservationForm.generalOptionsLoadFailed");
            } finally {
                if (!this.disposed) this.loading.general_options = false;
            }
        },
        tableSelected(entity) {
            if (entity.type === "table") {
                this.reserveTable(entity);
            } else if (entity.type === "group") {
                this.reserveGroup(entity);
            }
        },
        tableUnselected(entity, checks = true) {
            if (entity.type === "table") {
                this.releaseTable(entity, checks);
            } else if (entity.type === "group") {
                this.releaseGroup(entity, checks);
            }
        },
        isTableFromBaseReservation(table) {
            if (this.baseReservation) {
                const baseReservationTablesId = this.baseReservation.tables.data.map((t) => t.id);

                return baseReservationTablesId.includes(table.id);
            }

            return false;
        },
        isGroupFromBaseReservation(group) {
            if (this.baseReservation) {
                const baseReservationTablesId = this.baseReservation.tables.data.map((t) => t.id);

                return group.tables.filter((t) => !baseReservationTablesId.includes(t.id)).length === 0;
            }
            return false;
        },
        holdContext(entity) {
            return {
                url:
                    "/api/restaurants/" +
                    this.restaurant_id +
                    "/services/slots/" +
                    this.reservation.slot_id +
                    "/" +
                    (entity.type === "group" ? "groups" : "tables") +
                    "/reserved/new_resa/" +
                    entity.id,
                params: {
                    date: this.reservation.reservation_date.toISODate(),
                    duration: this.reservation.duration.HH + ":" + this.reservation.duration.mm,
                    pax: this.nb_pers_total,
                },
            };
        },
        async releaseEntity(entity) {
            const context = this.holdContext(entity);
            this.holdsPending++;
            try {
                await this.tableHolds.release(context.url, context.params);
            } catch (error) {
                if (!this.disposed) {
                    this.notifyError(error);
                    if (!this.vueMultiSelect.entitiesSelected.some((item) => item.type === entity.type && item.id === entity.id)) {
                        this.vueMultiSelect.entitiesSelected = [...this.vueMultiSelect.entitiesSelected, entity];
                    }
                }
            } finally {
                if (!this.disposed) this.holdsPending--;
            }
        },
        async reserveEntity(entity) {
            const context = this.holdContext(entity);
            this.holdsPending++;
            try {
                await this.tableHolds.reserve(context.url, context.params);
            } catch (error) {
                if (!this.disposed) {
                    this.notifyError(error);
                    this.vueMultiSelect.entitiesSelected = this.vueMultiSelect.entitiesSelected.filter(
                        (item) => !(item.type === entity.type && item.id === entity.id)
                    );
                }
            } finally {
                if (!this.disposed) this.holdsPending--;
            }
        },
        releaseTable(entity, checks = true) {
            if (!checks || !this.isTableFromBaseReservation(entity)) return this.releaseEntity(entity);
        },
        releaseGroup(entity, checks = true) {
            if (!checks || !this.isGroupFromBaseReservation(entity)) return this.releaseEntity(entity);
        },
        reserveTable(entity) {
            if (!this.isTableFromBaseReservation(entity)) return this.reserveEntity(entity);
        },
        reserveGroup(entity) {
            if (!this.isGroupFromBaseReservation(entity)) return this.reserveEntity(entity);
        },
        checkVueMultiSelectDisabledTables() {
            this.vueMultiSelect.isInvalid = this.vueMultiSelect.entitiesSelected.find((e) => e.$isDisabled) ? true : false;
        },
        checkVueMultiSelectPaxFull() {
            this.vueMultiSelect.paxFull = selectedTableCapacity(this.vueMultiSelect.entitiesSelected) >= this.nb_pers_total;
        },
        hasErrors(name) {
            return this.formErrors && this.formErrors.errors && !_.isEmpty(this.formErrors.errors[name]);
        },
        registerServiceSockets() {
            if (this.reservation.service && this.reservation.reservation_date && this.restaurant_id) {
                const channelName = `App.restaurant.${this.restaurant_id}.service.${
                    this.reservation.service.id
                }.date.${this.reservation.reservation_date.toISODate()}`;

                Echo.private(channelName).listen(".table.updated", this.onTableUpdate);

                this.socket.listeningChannels.services.push(channelName);
                this.$store.commit("sockets/addChannel", channelName);
            }
        },
        unregisterServiceSockets() {
            this.socket.listeningChannels.services.forEach((channelName) => {
                Echo.private(channelName).stopListening(".table.updated", this.onTableUpdate);
            });

            this.socket.listeningChannels.services = [];
        },
        onTableUpdate(event) {
            const table = event.table;
            const slotsIdsToDisable = table.reservations_pivot.data
                .map((rp) => {
                    return rp.all_slots_used.data.map((s) => s.id);
                })
                .flat()
                .filter((value, index, self) => self.indexOf(value) === index);

            if (this.seatingPlan) {
                const roomFound = this.seatingPlan.rooms.find((r) => r.id === table.room.id);

                if (roomFound) {
                    const tableFound = roomFound.entities.find((e) => e.type === "table" && e.id === table.id);

                    if (tableFound) {
                        tableFound.slots.forEach((s) => {
                            if (slotsIdsToDisable.includes(s.id)) {
                                s.entityEnabled = false;
                            } else {
                                s.entityEnabled = true;
                            }
                        });
                    }

                    const groupsFound = roomFound.entities.filter((e) => {
                        return e.type === "group" && e.tables.find((t) => t.id === table.id);
                    });

                    groupsFound.forEach((group) => {
                        const groupTableFound = group.tables.find((t) => t.id === table.id);

                        if (groupTableFound) {
                            groupTableFound.slots.forEach((s) => {
                                if (slotsIdsToDisable.includes(s.id)) {
                                    s.entityEnabled = false;
                                } else {
                                    s.entityEnabled = true;
                                }
                            });
                        }

                        group.slots.forEach((s) => {
                            if (group.tables.some((table) => !table.slots.find((slot) => slot.id === s.id)?.entityEnabled)) {
                                s.entityEnabled = false;
                            } else {
                                s.entityEnabled = true;
                            }
                        });
                    });
                }
            }
        },
        unselectAllTables() {
            return this.tableHolds.close();
        },
        scheduleChecks() {
            clearTimeout(this.checksTimer);
            this.checksGate.cancel();
            this.loading.scheduled = true;
            this.loading.can_place = false;
            this.loading.capacity = false;
            this.can_place = false;
            this.capacityStatus = "checking";
            this.$emit("enable-next-button", false);
            this.checksTimer = setTimeout(() => this.runChecks(), 160);
        },
        async runChecks() {
            this.loading.scheduled = false;
            const duration = this.reservation.duration;
            if (
                !this.reservation.slot_id ||
                !this.reservation.reservation_date ||
                !Number.isInteger(this.reservation.nb_pers) ||
                this.reservation.nb_pers < 1 ||
                !Number.isInteger(this.reservation.nb_children) ||
                this.reservation.nb_children < 0 ||
                !duration ||
                !/^\d{2}$/.test(duration.HH) ||
                !/^\d{2}$/.test(duration.mm)
            )
                return;
            const request = this.checksGate.begin();
            const params = {
                total: this.nb_pers_total,
                date: this.reservation.reservation_date.toISODate(),
                slot_id: this.reservation.slot_id,
                duration: duration.HH + ":" + duration.mm,
                ...(this.baseReservation ? { reservation_id: this.baseReservation.id } : {}),
                ...(this.reservation.service_id ? { service_id: this.reservation.service_id } : {}),
            };
            this.errors.seatingPlan = null;
            this.seatingUnavailable = false;
            await Promise.all([this.checkSeatingPlan(params, request), this.checkMaxPax(params, request)]);
        },
        async checkSeatingPlan(params, request) {
            if (!this.reservation.service?.is_seating_plan_algorithm_enabled) {
                this.can_place = true;
                return;
            }
            this.loading.can_place = true;
            try {
                await axios.get("/api/restaurants/" + this.restaurant_id + "/reservations/checkseatingplan", { params, signal: request.signal });
                if (request.current()) this.can_place = true;
            } catch (error) {
                if (!request.current()) return;
                this.can_place = false;
                if (error.response?.status === 403) this.seatingUnavailable = true;
                else this.errors.seatingPlan = this.$t("reservationForm.requestFailed");
            } finally {
                if (request.current()) this.loading.can_place = false;
            }
        },
        async checkMaxPax(params, request) {
            this.loading.capacity = true;
            try {
                await axios.get("/api/restaurants/" + this.restaurant_id + "/reservations/checkmaxpax", { params, signal: request.signal });
                if (request.current()) this.capacityStatus = "available";
            } catch (error) {
                if (request.current()) this.capacityStatus = error.response?.status === 403 ? "full" : "error";
            } finally {
                if (request.current()) this.loading.capacity = false;
            }
        },
        setDuration() {
            if (this.reservation.is_duration_customized === false) {
                let foundDuration = false;

                if (this.reservation.service) {
                    this.reservation.service.paxDurations.data.forEach((item) => {
                        if (item.pax === this.nb_pers_total) {
                            const durationsArray = item.duration.split(":");

                            this.$set(this.reservation, "duration", {
                                HH: `0${durationsArray[0]}`.substr(-2),
                                mm: `0${durationsArray[1]}`.substr(-2),
                            });
                            foundDuration = true;
                        }
                    });

                    if (!foundDuration) {
                        const durationsArray = this.reservation.service.duration.split(":");

                        this.$set(this.reservation, "duration", {
                            HH: `0${durationsArray[0]}`.substr(-2),
                            mm: `0${durationsArray[1]}`.substr(-2),
                        });
                    }
                }
            }
        },
    },
    watch: {
        checkKey: {
            immediate: true,
            handler() {
                this.scheduleChecks();
            },
        },
        serviceContextKey: {
            immediate: true,
            handler() {
                this.unregisterServiceSockets();
                this.registerServiceSockets();
                if (this.reservation.service && !this.isPassingCustomer) {
                    this.loadMenusData();
                    this.loadGeneralOptionsData();
                }
            },
        },
        nb_pers_total() {
            this.checkVueMultiSelectPaxFull();
            this.setDuration();
        },
        "vueMultiSelect.entitiesSelected": {
            deep: true,
            handler() {
                this.checkVueMultiSelectPaxFull();
                this.checkVueMultiSelectDisabledTables();
            },
        },
        canContinue: {
            immediate: true,
            handler(value) {
                this.$emit("enable-next-button", value);
            },
        },
        blocker: {
            immediate: true,
            handler(value) {
                this.$emit("blocker", value);
            },
        },
        isBusy: {
            immediate: true,
            handler(value) {
                this.$emit("busy", value);
            },
        },
        capacityStatus: {
            immediate: true,
            handler(value) {
                this.$emit("capacity-status", value);
            },
        },
    },
    components: {
        ReservationTablePicker,
        menusReservation,
        generalOptionsReservation,
    },
    created() {
        this.checkVueMultiSelectPaxFull();
        this.setDuration();
        if (!this.vueMultiSelect.entitiesSelected.length && this.tablesIdToPreselect.length) {
            this.setMultiSelectSelectedEntities(
                this.tableRooms
                    .flatMap((room) => room.entities)
                    .filter((entity) => entity.type === "table" && !entity.$isDisabled && this.tablesIdToPreselect.includes(entity.id))
            );
        }
    },
    beforeUnmount() {
        this.disposed = true;
        clearTimeout(this.checksTimer);
        this.checksGate.cancel();
        this.resourcesController.abort();
        this.unregisterServiceSockets();
        this.unselectAllTables();
    },
};
</script>

<style></style>
